import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusPill, { type PillVariant } from '../components/StatusPill';
import { useActiveCompany } from '../state';
import ExportModal from '../components/ExportModal';
import { getDocumentContent } from '../../lib/entities/documents';
import { uploadDocumentFile } from '../../lib/uploads';
import { formatDate } from '../../lib/entities/format';
import {
  getChecklistDocument,
  CATEGORY_TO_KEBAB,
  type ChecklistDocumentDetail,
  type ChecklistFinding,
} from '../../lib/entities/checklist';
import { CATEGORY_EXPLAINER, CATEGORY_META } from '../data';
import { Brand, brandify } from '../../components/Brand';
import { Alert, Doc, Download, Swap, Tick, Upload } from '../components/AppIcons';
import { DocxEditorPane, type DocxFixHandle } from '../components/DocxEditorPane';
import { DocumentFixList } from '../components/DocumentFixList';
import { STUB_FIXES, type DocumentFix } from '../components/documentFixes';
import { acceptAllTrackedChanges } from '../components/docxReview';
import { AddDocumentModal } from './DocumentsPage';
import {
  applyOverrideToDetail,
  canDevOverride,
  DEV_STATES,
  overrideFor,
  setOverride,
} from '../devStatus';
import '../../styles/app-documents.css';

/** API base for the direct upload flow (see uploads.ts); dev proxies `''` → the backend. */
const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '';

/** Human file size without a library: bytes → "125 B" / "8 KB" / "1.2 MB". */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** One assessment finding: what's wrong and how to fix it. Findings are always issues. */
function FindingRow({ finding }: { finding: ChecklistFinding }) {
  return (
    <div className={`docs-find docs-find--${finding.severity}`}>
      <span className={`docs-mark docs-mark--${finding.severity}`}>!</span>
      <span className="docs-find__text">
        {finding.title ? <strong>{finding.title}. </strong> : null}
        {finding.text}
        {finding.action && (
          <span className="docs-find__fix">
            <strong>How to fix:</strong> {finding.action}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * Dev-only: flip this document between requirement states, to review the colours without contriving
 * real uploads and assessments. Display only — see devStatus.ts. Localhost dev builds only, and styled
 * as an obvious debug affordance so it can never be mistaken for a product control.
 *
 * The state set here follows the document onto the requirements list, so the card edge can be checked
 * in situ rather than only in the status box above.
 */
function DevStatusBar({ typeId }: { typeId: string }) {
  if (!canDevOverride()) return null;
  const active = overrideFor(typeId);

  return (
    <div className="docs-devbar">
      <span className="docs-devbar__label">Dev: show as</span>
      {DEV_STATES.map((s) => (
        <button
          key={s.key}
          type="button"
          className={`docs-devbar__btn${active === s.key ? ' is-active' : ''}`}
          onClick={() => setOverride(typeId, active === s.key ? null : s.key)}
        >
          {s.label}
        </button>
      ))}
      {active && (
        <button
          type="button"
          className="docs-devbar__btn docs-devbar__btn--clear"
          onClick={() => setOverride(typeId, null)}
        >
          Reset
        </button>
      )}
    </div>
  );
}

/** The four visual states of the status box, plus the two non-needed resolutions. */
type StatusTone = 'red' | 'amber' | 'teal' | 'green' | 'muted' | 'danger';

interface StatusView {
  tone: StatusTone;
  title: string;
  sub: string;
  /** Work is in progress: swaps the icon for a spinner so the state reads as active, not stuck. */
  checking?: boolean;
}

/**
 * The detail render for one resolved document type. Everything shown is derived from the runtime
 * `detail` (category, status, why, checks, held instance) — nothing is read from a stored instance
 * category or from dummy data. The status box up top gives at-a-glance status and the next action to
 * take; the right-hand pane shows the actual file, with Download and Replace above it.
 */
function DocumentDetail({
  detail,
  onChanged,
}: {
  detail: ChecklistDocumentDetail;
  onChanged: () => void;
}) {
  const { companyId } = useActiveCompany();
  const [modal, setModal] = useState<null | 'upload' | 'replace'>(null);
  const [exporting, setExporting] = useState(false);
  const [docxBuffer, setDocxBuffer] = useState<ArrayBuffer | null>(null);
  const [docxError, setDocxError] = useState(false);
  const docxReq = useRef(0);

  // ── SPIKE: compliance-fix overlays on the editor (hardcoded findings; see documentFixes.ts). ──
  const [fixes, setFixes] = useState<DocumentFix[]>(STUB_FIXES);
  const [activeFix, setActiveFix] = useState<string | null>(null);
  const [locatedFixIds, setLocatedFixIds] = useState<Set<string>>(new Set());
  const fixHandle = useRef<DocxFixHandle>(null);
  // Edit-review state: the editor opens in suggesting mode on an accepted baseline, so any tracked change
  // (a reeve fix the user applied, or their own edit) is a pending change they can Accept (save + re-check)
  // or Revert (reload from storage). Nothing is written until they Accept.
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { instance, findings } = detail;
  // Null category means needsAnswer (a company doc shouldn't reach this, but handle it rather than crash).
  const categoryLabel = detail.category
    ? CATEGORY_META[CATEGORY_TO_KEBAB[detail.category]].label
    : 'Needs an answer';
  // Every finding is an issue to fix (critical/important/suggestion); passed checks produce no finding.
  const fixItems = findings;
  const fixCount = fixItems.length;

  const catVariant: PillVariant =
    detail.category === 'shouldNotHave'
      ? 'danger'
      : detail.category === 'legallyRequired'
        ? 'rose'
        : 'muted';

  // Load the held file's bytes SAME-ORIGIN (the SAS url is CORS-blocked for fetch) so the editor can open it.
  // reeve stores only .docx, so a held instance always opens in the editor; a replace (new version) re-fetches.
  // Every change already in the file is ACCEPTED first (headless), so the editor opens on a clean baseline and
  // the only pending changes are the ones reeve/the user then make. A fetch failure flips to a download offer.
  useEffect(() => {
    setDocxBuffer(null);
    setDocxError(false);
    setDirty(false);
    if (!instance) return;
    const id = ++docxReq.current;
    getDocumentContent(instance.documentId)
      .then((buf) => acceptAllTrackedChanges(buf))
      .then((buf) => {
        if (id === docxReq.current) setDocxBuffer(buf);
      })
      .catch(() => {
        if (id === docxReq.current) setDocxError(true);
      });
  }, [instance?.documentId, instance?.version, instance]);

  // The status box tone + copy, from the resolved status (and, for a held document, whether an
  // assessment has flagged anything to fix).
  const view: StatusView = (() => {
    if (detail.status === 'shouldNotHave') {
      return {
        tone: 'danger',
        title: 'You shouldn’t hold this',
        sub: 'Unlawful, unenforceable or misleading for a business like yours. Remove or replace it.',
      };
    }
    if (detail.status === 'inert') {
      return {
        tone: 'muted',
        title: 'Not needed',
        sub: 'Fine to skip for a business like yours. reeve will add it to your requirements if that changes.',
      };
    }
    if (detail.status === 'missing') {
      return {
        tone: 'red',
        title: 'Not in place yet',
        sub: `This is ${categoryLabel.toLowerCase()}. Upload the version you use today to put it in place.`,
      };
    }
    if (fixCount > 0) {
      return {
        tone: 'amber',
        title: `In place, ${fixCount} thing${fixCount === 1 ? '' : 's'} to fix`,
        sub: 'reeve checked it against current law and found a few things to sort out.',
      };
    }
    // Held, but nothing has read what's on file yet. This has to come BEFORE the green case: an empty
    // findings list means "checked, nothing wrong" and "not read yet" equally, and the page used to
    // say "In place and checked" for both — telling the user reeve had read a document it hadn't.
    if (detail.health === 'unchecked') {
      return {
        // Teal, not amber: matching the card edge, and off the good-to-bad scale because no verdict
        // has been reached yet.
        tone: 'teal',
        checking: true,
        title: 'In place, checking it now',
        sub: 'It’s on file. reeve is reading it against current law and will show anything to fix here as soon as it’s done.',
      };
    }
    return {
      tone: 'green',
      title: 'In place and checked',
      sub: 'reeve re-checks it whenever you replace it or the law changes.',
    };
  })();

  // Download is a format picker (ExportModal): reeve holds one canonical copy and hands it back as Word or
  // PDF on demand. Opening the picker needs no network call; the fetch + optional convert happens on choice.
  const openExport = useCallback(() => setExporting(true), []);

  // The box always shows the next step to progress: Upload when nothing's on file, the list of things
  // to fix (rendered inline, below) when it's held but non-compliant, Download when it's all good.
  const statusAction = (() => {
    if (view.tone === 'red') {
      return (
        <div className="docs-status__actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setModal('upload')}>
            <Upload size={16} />
            Upload
          </button>
          {detail.templateAvailable && (
            <Link to={`/app/documents/${detail.id}/template`} className="btn btn-secondary btn-sm">
              Use a template
            </Link>
          )}
        </div>
      );
    }
    // Download is offered while checking too: the file is on file and theirs, and waiting on a check is
    // no reason to withhold it.
    if ((view.tone === 'green' || view.checking) && instance) {
      return (
        <button type="button" className="btn btn-green-soft btn-sm" onClick={openExport}>
          <Download size={16} />
          Download
        </button>
      );
    }
    return null;
  })();

  // Null when the tier is unresolved (needsAnswer): there is no label to explain until a predicate
  // this document depends on is answered.
  const tierExplainer = detail.category
    ? CATEGORY_EXPLAINER[CATEGORY_TO_KEBAB[detail.category]]
    : null;

  const whyTitle =
    detail.category === 'shouldNotHave'
      ? 'Why you shouldn’t have this'
      : detail.category === 'notRelevant'
        ? 'Why you can skip this'
        : 'Why you need this';

  const statusIcon = view.checking ? (
    // A spinner rather than "!": nothing is wrong, reeve is simply still reading it.
    <span className="docs-spin" aria-hidden="true" />
  ) : view.tone === 'amber' ? (
    <span aria-hidden="true">!</span>
  ) : view.tone === 'danger' || view.tone === 'red' ? (
    <Alert size={18} />
  ) : (
    <Tick size={18} />
  );

  // Round icon actions next to the title — Replace and Download for a held file, easy to hit. Download
  // opens the format picker (Word / PDF); there's no separate per-format button.
  const fileActions = instance ? (
    <div className="docs-fileactions">
      <button
        type="button"
        className="iconbtn"
        onClick={() => setModal('replace')}
        aria-label="Replace file"
        title="Replace file"
      >
        <Swap size={18} />
      </button>
      <button
        type="button"
        className="iconbtn iconbtn--primary"
        onClick={openExport}
        aria-label="Download"
        title="Download"
      >
        <Download size={18} />
      </button>
    </div>
  ) : undefined;

  // The editor opens `docxBuffer` (a held .docx, fetched same-origin above). Non-docx files (a PDF, until
  // the conversion pipeline lands) leave it null, so DocxEditorPane shows the file viewer instead.
  // The editor's auto-save callback is a no-op: reeve saves explicitly via Accept, so edits stay in the
  // editor until the user commits them.
  const handleDocxSave = () => {};

  // Accept: finalize every tracked change into the text, write the new version back to storage, then let the
  // detail refetch re-run the compliance checks.
  const acceptEdits = useCallback(async () => {
    if (!instance || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const buffer = await fixHandle.current?.acceptAndSave();
      if (!buffer) throw new Error('Could not read the edited document.');
      const file = new File([buffer], instance.fileName, { type: instance.contentType });
      await uploadDocumentFile(API_BASE, instance.documentId, file);
      setDirty(false);
      onChanged(); // new version + re-assessment
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save your changes.');
    } finally {
      setSaving(false);
    }
  }, [instance, saving, onChanged]);

  // Revert: throw the in-editor edits away by reloading the stored (accepted) version.
  const revertEdits = useCallback(async () => {
    if (!instance || saving) return;
    setSaveError(null);
    try {
      const buf = await acceptAllTrackedChanges(await getDocumentContent(instance.documentId));
      fixHandle.current?.reload(buf);
      setFixes(STUB_FIXES); // restore any fixes that were applied then reverted
      setDirty(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not revert your changes.');
    }
  }, [instance, saving]);

  return (
    <div className="apage apage--wide apage--doc">
      {/* Back to the tab this document actually lives on. A bare /app/documents dumped the user on the
          first tab, which threw away where they were in a list of eighty things. Derived from the
          document rather than remembered from the list, so a deep link or a search result lands
          somewhere sensible too. */}
      <Link to={`/app/documents?tab=${detail.area}`} className="docs-back">
        ← All documents
      </Link>

      <PageHeader
        eyebrow={detail.family || 'Documents'}
        title={detail.name}
        lead={
          <span className="docs-headmeta">
            <StatusPill variant={catVariant} dot={false}>
              {categoryLabel}
            </StatusPill>
          </span>
        }
        actions={fileActions}
      />

      <div className="docs-split docs-split--doc">
        <div className="docs-detail-left">
          <DevStatusBar typeId={detail.id} />

          {/* Status box: the top info box — current state + the next action to take. */}
          <section className={`docs-status docs-status--${view.tone}`}>
            <div className="docs-status__head">
              <span className="docs-status__icon">{statusIcon}</span>
              <strong className="docs-status__title">{view.title}</strong>
            </div>
            <span className="docs-status__sub">{view.sub}</span>
            {fixCount > 0 && (
              <div className="docs-status__fixes">
                {fixItems.map((finding, i) => (
                  <FindingRow key={i} finding={finding} />
                ))}
              </div>
            )}
            {statusAction && <div className="docs-status__action">{statusAction}</div>}
          </section>

          {/* SPIKE: the fixes reeve found, with jump-to-highlight and one-click apply. Anchored fixes get a
              "Jump to fix"; document-level ones don't; only auto-applicable ones get "Apply". */}
          {docxBuffer && fixes.length > 0 && (
            <section className="acard acard--pad">
              <h2 className="acard__title">
                <Brand /> found {fixes.length} {fixes.length === 1 ? 'thing' : 'things'} to fix
              </h2>
              <DocumentFixList
                fixes={fixes}
                locatedIds={locatedFixIds}
                activeId={activeFix}
                onActivate={setActiveFix}
                onJump={(id) => fixHandle.current?.jumpTo(id)}
                onApply={(id) => fixHandle.current?.applyFix(id)}
                onReopen={(id) => fixHandle.current?.reopen(id)}
              />
            </section>
          )}

          {/* Why you need this (or shouldn't have / can skip). */}
          <section className="acard acard--pad">
            <h2 className="acard__title">{whyTitle}</h2>
            <p className="docs-card-body">{detail.why}</p>
          </section>

          {/* What the TIER means, generically. The box above says why this particular document matters;
              this one explains the label attached to it, which is otherwise jargon a user is expected to
              have absorbed from a section heading. Absent when the tier is unresolved, because there is
              no label to explain yet. */}
          {tierExplainer && (
            <section className="acard acard--pad docs-tier">
              <h2 className="acard__title">{tierExplainer.title}</h2>
              <p className="docs-card-body">{brandify(tierExplainer.body)}</p>
            </section>
          )}

          {/* What reeve checks — the rubric for this document type. No manual re-check: reeve checks
              on upload, when the document changes, and when the law changes. */}
          {detail.checks.length > 0 && (
            <section className="acard acard--pad">
              <h2 className="acard__title">
                What <Brand /> checks
              </h2>
              <p className="acard__sub">
                reeve checks for these when you upload, when the document changes, and when the law
                changes.
              </p>
              <ul className="docs-checks">
                {detail.checks.map((check, i) => (
                  <li key={i} className="docs-check">
                    <span className="docs-check__mark">
                      <Tick size={12} />
                    </span>
                    <span>{check}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="docs-detail-right">
          {detail.status === 'shouldNotHave' && (
            <div className="docs-banner">
              <Alert size={20} />
              <p>
                <strong>You shouldn’t hold this document.</strong>
                It’s unlawful, unenforceable or misleading for a business like yours. Remove it, or
                replace it with the version reeve recommends.
              </p>
            </div>
          )}

          <div className="docs-viewer">
            <div className="docs-viewer__bar">
              <Doc size={15} />
              {instance ? (
                <>
                  <span className="docs-viewer__file">{instance.fileName}</span>
                  <span className="docs-viewer__meta">
                    {formatDate(instance.uploadedAt)} · {formatSize(instance.sizeBytes)}
                  </span>
                </>
              ) : (
                <span className="docs-viewer__file">No file on record</span>
              )}
              {dirty && (
                <div className="docs-viewer__actions">
                  {saveError && (
                    <span className="docs-viewer__err" role="alert">
                      {saveError}
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={saving}
                    onClick={() => void revertEdits()}
                  >
                    Revert
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={saving}
                    onClick={() => void acceptEdits()}
                  >
                    {saving ? 'Saving…' : 'Accept changes'}
                  </button>
                </div>
              )}
            </div>

            <DocxEditorPane
              ref={fixHandle}
              documentBuffer={docxBuffer}
              onSave={handleDocxSave}
              onDirtyChange={setDirty}
              fixes={fixes}
              activeFixId={activeFix}
              onActiveFixChange={setActiveFix}
              onLocatedChange={(ids) => setLocatedFixIds(new Set(ids))}
              onFixApplied={(id) => setFixes((prev) => prev.filter((f) => f.id !== id))}
              fallback={
                !instance ? (
                  detail.status === 'inert' ? (
                    <div className="docs-viewer__sheet docs-viewer__sheet--empty">
                      <div className="docs-viewer__cta">
                        <span className="docs-viewer__ctamark docs-viewer__ctamark--ok">
                          <Tick size={18} />
                        </span>
                        <p>
                          <strong>Nothing on file, and nothing needed.</strong> <Brand /> will move
                          this onto your requirements if your business changes.
                        </p>
                      </div>
                    </div>
                  ) : (
                <div className="docs-viewer__sheet docs-viewer__sheet--empty">
                  <div className="docs-viewer__cta">
                    <span className="docs-viewer__ctamark">
                      <Upload size={18} />
                    </span>
                    <p>
                      <strong>Nothing on file yet.</strong> Upload the version you use today and{' '}
                      <Brand /> will check it for you.
                    </p>
                    <div className="docs-viewer__ctabtns">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setModal('upload')}
                      >
                        <Upload size={16} />
                        Upload
                      </button>
                      {detail.templateAvailable && (
                        <Link
                          to={`/app/documents/${detail.id}/template`}
                          className="btn btn-secondary btn-sm"
                        >
                          Use a template
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )
            ) : docxError ? (
              <div className="docs-viewer__sheet docs-viewer__sheet--empty">
                <div className="docs-viewer__cta">
                  <span className="docs-viewer__ctamark docs-viewer__ctamark--doc">
                    <Doc size={18} />
                  </span>
                  <p>
                    We couldn’t open <strong>{instance.fileName}</strong> here. Download it to open
                    the full file.
                  </p>
                  <div className="docs-viewer__ctabtns">
                    <button type="button" className="btn btn-green-soft btn-sm" onClick={openExport}>
                      <Download size={16} />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="docs-viewer__sheet docs-viewer__sheet--empty">
                <div className="docs-viewer__cta">
                  <p>Loading document…</p>
                </div>
              </div>
            )
              }
            />
          </div>
        </div>
      </div>

      {modal && (
        <AddDocumentModal
          title={modal === 'replace' ? 'Replace this file' : `Upload your ${detail.name.toLowerCase()}`}
          companyId={companyId ?? ''}
          documentId={modal === 'replace' ? instance?.documentId : undefined}
          catalogId={modal === 'upload' ? detail.id : undefined}
          defaultName={detail.name}
          onClose={() => setModal(null)}
          onUploaded={onChanged}
        />
      )}

      {exporting && instance && (
        <ExportModal instance={instance} onClose={() => setExporting(false)} />
      )}
    </div>
  );
}

export default function DocumentDetailPage() {
  const { slug } = useParams();
  const { companyId, company, companyLoading, refetchChecklist, refetchDocuments } =
    useActiveCompany();
  const [detail, setDetail] = useState<ChecklistDocumentDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Request-id guard so a slow load can't clobber a newer one when the slug/company changes.
  const reqId = useRef(0);

  // The dev override is applied at render, so a click has to re-render this component to take effect.
  const [, bumpOverrides] = useState(0);
  useEffect(() => {
    const onChange = () => bumpOverrides((n) => n + 1);
    window.addEventListener('reeve-dev-override', onChange);
    return () => window.removeEventListener('reeve-dev-override', onChange);
  }, []);

  const load = useCallback(async () => {
    if (!companyId || !slug) return;
    const id = ++reqId.current;
    try {
      const data = await getChecklistDocument(companyId, slug);
      if (id !== reqId.current) return;
      setDetail(data);
      setState('ready');
    } catch (err) {
      if (id !== reqId.current) return;
      setDetail(null);
      setErrorMsg(err instanceof Error ? err.message : 'Could not load this document.');
      setState('error');
    }
  }, [companyId, slug]);

  // Show the loading shell only when the slug/company CHANGES. onChanged() calls load() directly
  // (not via this effect), so an upload/replace refetch swaps the content in place — the page never
  // flashes back to "Loading…" and any open modal (and its success screen) stays mounted.
  useEffect(() => {
    setState('loading');
    setDetail(null);
    void load();
  }, [load]);

  // After an upload/replace: re-resolve THIS document AND refresh the shared checklist so the
  // documents page and dashboard move in step.
  const onChanged = useCallback(() => {
    void load();
    void refetchChecklist();
    void refetchDocuments();
  }, [load, refetchChecklist, refetchDocuments]);

  if (state === 'ready' && detail) {
    // Dev-only display override (devStatus.ts). Applied at render, never fetched or stored, so the
    // page shows the state without the product ever believing it.
    const shown = canDevOverride() ? applyOverrideToDetail(detail) : detail;
    return <DocumentDetail key={detail.id} detail={shown} onChanged={onChanged} />;
  }

  // No active company (and not mid-load) is a terminal empty state, not a spinner — otherwise the page
  // would sit on "Loading…" forever, since load() early-returns without a company.
  const noCompany = !companyId && !companyLoading;
  const busy = !noCompany && (state === 'loading' || companyLoading);

  return (
    <div className="apage">
      <Link to="/app/documents" className="docs-back">
        ← All documents
      </Link>
      <PageHeader
        eyebrow="Documents"
        title={noCompany ? 'Documents' : busy ? 'Loading…' : 'We can’t find that document'}
      />
      <div className="acard">
        <div className="empty">
          {noCompany ? (
            'Add a company to see its documents.'
          ) : busy ? (
            'Loading this document…'
          ) : (
            <>
              {errorMsg ??
                `Nothing on ${company ? `${company.name}’s` : 'your'} requirements match this link.`}
              <div className="docs-empty-actions">
                <Link to="/app/documents" className="btn btn-secondary btn-sm">
                  Back to all documents
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
